/**
 * PAYG top-up packs — single source of truth for price, energy granted, and
 * volume bonus. Imported by worker (billing stamps checkout metadata) and
 * client (SettingsView + landing Pricing render the same numbers), so the
 * energy shown always matches what the server grants.
 *
 * Stripe Price stays $5/$10/$20 (aligned to the pack label). The energy bonus
 * is granted server-side via TOPUP_PACK_ENERGY, decoupled from the Price — so
 * changing the bonus is a one-file edit here, no Stripe migration.
 *
 * Bonus depths (0/20/35%) follow the canonical consumer hard-currency curve
 * (e.g. mobile-game gem packs): anchor tier clean, escalating bonus pulls
 * buyers up the ladder. 2026-07-27.
 */

export type TopupAmount = '5' | '10' | '20'

export interface TopupPack {
  /** Pack label, sent to /checkout and used as the Stripe lookup key suffix. */
  amount: TopupAmount
  /** USD price in cents — matches the Stripe Price unit_amount. */
  priceCents: number
  /** Total energy granted (base + bonus). 1 energy = 1 cent nominal. */
  energy: number
  /** Bonus energy over the linear base (amount × 100). */
  bonusEnergy: number
  /** Bonus as a rounded percentage of the linear base. 0 at the anchor. */
  bonusPercent: number
  /** True for the best per-unit value tier (the $20 pack). */
  bestValue: boolean
}

/** Energy granted per pack. Worker reads this to stamp checkout metadata. */
export const TOPUP_PACK_ENERGY: Readonly<Record<TopupAmount, number>> = Object.freeze({
  '5': 500,
  '10': 1200, // +20% vs linear 1000
  '20': 2700, // +35% vs linear 2000
})

/** Ordered pack metadata for UI rendering. */
export const TOPUP_PACKS: readonly TopupPack[] = Object.freeze([
  { amount: '5', priceCents: 500, energy: 500, bonusEnergy: 0, bonusPercent: 0, bestValue: false },
  { amount: '10', priceCents: 1000, energy: 1200, bonusEnergy: 200, bonusPercent: 20, bestValue: false },
  { amount: '20', priceCents: 2000, energy: 2700, bonusEnergy: 700, bonusPercent: 35, bestValue: true },
])

/** Look up a pack by its label. */
export function getTopupPack(amount: string): TopupPack | undefined {
  return TOPUP_PACKS.find((p) => p.amount === amount)
}
